/**
 * Saidiseire (customer site monitoring): the CRM's side of the contract.
 *
 * A HAND-WRITTEN MIRROR of `apps/api/src/modules/crm-monitoring/types.ts`
 * (every view below) and of the route table in `docs/saidiseire-plaan.md`.
 * `monitoring.test.ts` reads the API file and compares the field names of each
 * interface, so a field added on the server fails a test here instead of
 * silently never reaching the screen.
 *
 * React-free on purpose. Everything a screen has to DECIDE - which tone a state
 * wears, how a day with no data is drawn, when an expiry turns urgent, what a
 * typed address becomes, what a package gives a site, which sentence a refusal
 * gets, how the response chart zooms, scrolls and tiles its history, what a
 * typed setting becomes - lives in the helper files next to this one
 * (`monitoringState.ts`, `monitoringView.ts`, `monitoringUrl.ts`,
 * `monitoringPlans.ts`, `monitoringErrors.ts`, `monitoringSeries.ts`,
 * `monitoringTiles.ts`, `monitoringSettings.ts`) and is re-exported here, so
 * the section imports one path and the rules stay testable without mounting
 * anything.
 */
import { api } from "./api";

export * from "./monitoringState";
export * from "./monitoringView";
export * from "./monitoringUrl";
export * from "./monitoringPlans";
export * from "./monitoringErrors";
export * from "./monitoringSeries";
export * from "./monitoringTiles";
export * from "./monitoringSettings";

// ── Contract (mirrors crm-monitoring/types.ts) ─────────────────────────────────

export type MonitorState = "up" | "down" | "blocked" | "unknown";

/**
 * The rules the engine applies to one site, from its web maintenance package
 * (a Veebihaldus plan; docs/saidiseire-paketid.md).
 */
export interface MonitorPolicyView {
  /** "plan": the site's package decides; "default": no package (or it is gone), the Saidiseire settings do. */
  source: "plan" | "default";
  /** Minutes between two checks of a healthy site. */
  checkMinutes: number;
  certExpiry: boolean;
  domainExpiry: boolean;
  /** A WordPress reporter key may be issued. */
  wpReporter: boolean;
  /** PageSpeed Insights once a week. False without a package. */
  speedTest: boolean;
  /** The weekly security scan. False without a package. */
  securityScan: boolean;
  /** The monthly status report. False without a package. */
  monthlyReport: boolean;
  /** Each update judged by the backups before it (item J). False without a package. */
  backupBeforeUpdate: boolean;
  /** A database backup at most 36 h old (item J). False without a package. */
  backupDaily: boolean;
  /** A scheduled database clean-up (item J). False without a package. */
  dbCleanup: boolean;
}

/** A web maintenance package a site can be put on. */
export interface MonitorPlanView {
  code: string;
  /** The package's name per language (`et`, `en`). */
  name: Record<string, string>;
  /** False for a package hidden from the public price list; a site can still be on it. */
  isPublic: boolean;
  policy: MonitorPolicyView;
}

export interface MonitorPlansResponse {
  /** In the price list's order. */
  plans: MonitorPlanView[];
  /** The rules for a site without a package. */
  defaults: MonitorPolicyView;
}

export interface MonitorIncidentView {
  id: string;
  kind: "down";
  startedAt: string;
  confirmedAt: string | null;
  resolvedAt: string | null;
  code: string | null;
  /**
   * Why the engine closed it without seeing the site up: "paused",
   * "client_inactive" or "engine_off". Null while open, or when the site
   * recovered. A closed one with a reason is not a recovery.
   */
  endedBy: string | null;
  /** Seconds from start to resolve (or to now while open). */
  durationSeconds: number;
}

export interface MonitorExpiryView {
  /** ISO date of the certificate's end or the domain's expiry. */
  at: string | null;
  verdict: string | null;
  /** Calendar days left in Europe/Tallinn, negative once past. */
  daysLeft: number | null;
  checkedAt: string | null;
}

export interface MonitorRow {
  id: string;
  clientId: string;
  clientName: string;
  url: string;
  host: string;
  label: string | null;
  /** The web maintenance package (`MonitorPlanView.code`), null for none. */
  planCode: string | null;
  /** The client's project the site's maintenance is logged in; the monthly report's work done lists its time. */
  projectId: string | null;
  /** The platform site when the watched site runs on our own platform, else null (`monitoringPlatform.ts`). */
  platformSiteId: string | null;
  paused: boolean;
  /** False for a monitor of an inactive client: it is not checked, like a paused one. */
  clientActive: boolean;
  /**
   * `down` on a site seen working means a confirmed outage (the bell counts
   * it). A site never seen working (`lastOkAt` null) shows its last outcome,
   * `down` included, and opens no outage.
   */
  state: MonitorState;
  stateSince: string | null;
  lastCheckAt: string | null;
  /** The last up check. Null = never seen working. */
  lastOkAt: string | null;
  lastCode: string | null;
  lastStatus: number | null;
  lastMs: number | null;
  /** Uptime over the last 30 days, 0..100, null when nothing counted. */
  uptime30d: number | null;
  /** Median time to first byte over the last 7 days. */
  p50Ms7d: number | null;
  openIncident: MonitorIncidentView | null;
  cert: MonitorExpiryView | null;
  domain: MonitorExpiryView | null;
}

export interface MonitorDayView {
  day: string;
  checks: number;
  upChecks: number;
  downChecks: number;
  blockedChecks: number;
  uptime: number | null;
  p50Ms: number | null;
  p95Ms: number | null;
  downSeconds: number;
}

export interface MonitorCheckView {
  at: string;
  outcome: MonitorState;
  code: string | null;
  status: number | null;
  ms: number | null;
}

/** Stopped monitors (paused or of an inactive client) count as `paused`; a site
 *  never seen working whose last check failed counts as `neverUp`, so `down` is
 *  exactly the confirmed outages the bell counts. */
export interface MonitorSummary {
  total: number;
  up: number;
  down: number;
  blocked: number;
  unknown: number;
  neverUp: number;
  paused: number;
}

export interface MonitorEngineStatus {
  enabled: boolean;
  intervalMinutes: number;
  lastBeatAt: string | null;
  lastBeatOk: boolean | null;
  lastBeatNote: string | null;
  heartbeatConfigured: boolean;
}

export interface MonitorListResponse {
  sites: MonitorRow[];
  summary: MonitorSummary;
  engine: MonitorEngineStatus;
}

export interface MonitorDetailResponse {
  site: MonitorRow;
  /** The rules the engine applies to this site right now. */
  policy: MonitorPolicyView;
  /** Last 90 days, oldest first; days without a row are absent (the UI draws them as unknown). */
  days: MonitorDayView[];
  /** Last 50 confirmed incidents, newest first. */
  incidents: MonitorIncidentView[];
  /** Last 24 hours of checks, oldest first. */
  checks: MonitorCheckView[];
  /**
   * The oldest check still kept for this site (raw checks are kept 45 days),
   * or null when it has none. How far back the chart can scroll.
   */
  historyFrom: string | null;
}

/**
 * One raw check (step 0) or one bucket of checks (step > 0) of the response
 * time chart (`GET /sites/:id/series`). The chart's arithmetic on these is
 * `monitoringSeries.ts`.
 */
export interface MonitorSeriesPoint {
  /** Epoch ms: the check's own time (step 0) or the bucket's start (a multiple of the step since the epoch). */
  t: number;
  /** Checks in it: 1 for a raw check. */
  n: number;
  up: number;
  down: number;
  blocked: number;
  unknown: number;
  /** Time to first byte over the up checks that carry a time; null when none did. Nearest-rank. */
  p50: number | null;
  p95: number | null;
  min: number | null;
  max: number | null;
  /** The code of the latest check in it that was not up; null when every check was up. */
  code: string | null;
}

/** A stretch the site was seen down, epoch ms. */
export interface MonitorDownSpan {
  from: number;
  to: number;
}

export interface MonitorSeriesResponse {
  /** The window actually read (aligned for step > 0), epoch ms. */
  from: number;
  to: number;
  /** Seconds per bucket; 0 = raw checks, one point per check. */
  step: number;
  /** Oldest first; only buckets that hold at least one check. */
  points: MonitorSeriesPoint[];
  /**
   * Down runs inside the window, merged. A span can end after `to` when the
   * check that closed it came after the window.
   */
  downSpans: MonitorDownSpan[];
  /**
   * The window's latest check was down and no check after the window exists
   * yet, so the last span ends at that failure only for now: a later read
   * moves its end. The chart keeps such a window open until it does not.
   */
  openEnd: boolean;
}

export interface MonitorSuggestion {
  clientId: string;
  clientName: string;
  website: string;
  /** Normalised URL the add dialog would save, or null when the website does not parse. */
  url: string | null;
}

export interface MonitorSettingsView {
  enabled: boolean;
  intervalMinutes: number;
  concurrency: number;
  recipientUserIds: string[];
  /** Staff who may receive alerts (active host-site members with view:crm). */
  candidates: { userId: string; name: string | null; email: string }[];
  heartbeatConfigured: boolean;
}

// ── Request bodies ───────────────────────────────────────────────────────────

export interface CreateMonitorBody {
  clientId: string;
  url: string;
  label?: string | null;
  planCode?: string | null;
  /** One of the client's own projects (the server refuses any other, `bad_project`). */
  projectId?: string | null;
  /** A site on our own platform; the address must be one of its verified domains (`bad_platform_site`). */
  platformSiteId?: string | null;
}

/** What PATCH /sites/:id accepts. The URL and the client are not in it: a
 *  different address is a different site, so the dialog shows both read-only
 *  when editing and the way to change them is remove and add again. */
export interface UpdateMonitorBody {
  label?: string | null;
  planCode?: string | null;
  projectId?: string | null;
  paused?: boolean;
}

export interface MonitorSettingsPatch {
  enabled?: boolean;
  intervalMinutes?: number;
  concurrency?: number;
  recipientUserIds?: string[];
  /** A new ping address, or null to remove it. Never read back. */
  heartbeatUrl?: string | null;
}

export interface CreateMonitorResult {
  site: MonitorRow;
  /** Other clients that already watch the same host. Allowed, but worth saying. */
  alsoWatchedBy: { clientId: string; clientName: string }[];
}

// ── Limits (shared with the route's zod) ─────────────────────────────────────

/** Mirrors the route's `label` rule: a site's own name, at most this long. */
export const MONITOR_LABEL_MAX = 120;

// ── Routes ───────────────────────────────────────────────────────────────────

export const MONITORING_PATH = "/admin/crm/monitoring";
export const MONITOR_SITES_PATH = `${MONITORING_PATH}/sites`;
export const MONITOR_SUGGESTIONS_PATH = `${MONITORING_PATH}/suggestions`;
export const MONITOR_SETTINGS_PATH = `${MONITORING_PATH}/settings`;
export const MONITOR_PLANS_PATH = `${MONITORING_PATH}/plans`;

const sitePath = (id: string) => `${MONITOR_SITES_PATH}/${encodeURIComponent(id)}`;

/** One site's response time series; the query (`from`, `to`, `step`) is `getMonitorSeries`'s. */
export const MONITOR_SERIES_PATH = (id: string): string => `${sitePath(id)}/series`;

export const listMonitors = (signal?: AbortSignal): Promise<MonitorListResponse> =>
  api.get<MonitorListResponse>(MONITOR_SITES_PATH, signal);

export const getMonitor = (id: string, signal?: AbortSignal): Promise<MonitorDetailResponse> =>
  api.get<MonitorDetailResponse>(sitePath(id), signal);

/**
 * One window of a site's checks: raw (`step` 0, at most `SERIES_RAW_MAX_MS`)
 * or in buckets of `step` seconds (at most `SERIES_MAX_BUCKETS`). The route
 * takes whole, non-negative epoch ms, so a window worked out from a zoom is
 * widened to whole milliseconds here rather than refused as `bad_query`.
 */
export const getMonitorSeries = (
  id: string,
  window: { from: number; to: number; step: number },
  signal?: AbortSignal,
): Promise<MonitorSeriesResponse> => {
  const qs = new URLSearchParams({
    from: String(Math.max(0, Math.floor(window.from))),
    to: String(Math.ceil(window.to)),
    step: String(window.step),
  });
  return api.get<MonitorSeriesResponse>(`${MONITOR_SERIES_PATH(id)}?${qs}`, signal);
};

export const createMonitor = (body: CreateMonitorBody): Promise<CreateMonitorResult> =>
  api.post<CreateMonitorResult>(MONITOR_SITES_PATH, body);

export const updateMonitor = (id: string, body: UpdateMonitorBody): Promise<{ site: MonitorRow }> =>
  api.patch<{ site: MonitorRow }>(sitePath(id), body);

export const deleteMonitor = (id: string): Promise<void> => api.del<void>(sitePath(id));

export const checkMonitorNow = (id: string): Promise<{ site: MonitorRow }> =>
  api.post<{ site: MonitorRow }>(`${sitePath(id)}/check`);

export const listMonitorSuggestions = (): Promise<{ suggestions: MonitorSuggestion[] }> =>
  api.get<{ suggestions: MonitorSuggestion[] }>(MONITOR_SUGGESTIONS_PATH);

export const listMonitorPlans = (signal?: AbortSignal): Promise<MonitorPlansResponse> =>
  api.get<MonitorPlansResponse>(MONITOR_PLANS_PATH, signal);

export const getMonitorSettings = (): Promise<MonitorSettingsView> =>
  api.get<MonitorSettingsView>(MONITOR_SETTINGS_PATH);

export const saveMonitorSettings = (patch: MonitorSettingsPatch): Promise<MonitorSettingsView> =>
  api.patch<MonitorSettingsView>(MONITOR_SETTINGS_PATH, patch);
